import { logger } from 'cadenza/utils/logging';
import {
  getLayerFeatureAttributes,
  getLayerFeatureAttributeValues,
  saveFeature,
  saveFeatures
} from 'cadenza/api-client/maps-api';
import { LayerProperty } from 'cadenza/map/layer-property';
import {
  AbstractFeaturePanel,
  NOTIFICATION_TYPE
} from 'cadenza/map/feature-attributes-panel/abstract-feature-panel';
import { refreshLayer } from 'cadenza/map/layer-utils';
import {
  FeatureAttributeFormGroup
} from 'cadenza/map/feature-attributes-panel/feature-attribute-form-group';

import i18n from './messages.properties';

const NAME = 'd-map-feature-attributes';

export class FeatureAttributesPanel extends AbstractFeaturePanel {

  #formGroups;
  #initialValues = {};

  constructor ({ map, layer, onClose } = {}) {
    super({ map, layer, title: i18n('title'), onClose, shouldConfirmPrimaryInteractionChange: true });
  }

  connectedCallback () {
    super.connectedCallback();
    this.classList.add(NAME);
    this._getFeatureAttributes();
  }

  _saveAttributes () {
    const layerId = this.selectedLayer.get(LayerProperty.ID);
    const featureIds = this.selectedLayer.get(LayerProperty.SELECTED_FEATURE_IDS);
    if (featureIds.length === 1) {
      const featureId = featureIds[0];
      return saveFeature(layerId, featureId, this._getFeatureDto(featureId));
    } else {
      const bulkUpdateFeatureDto = this._getBulkUpdateFeatureDto(featureIds);
      if (Object.keys(bulkUpdateFeatureDto.modifications).length !== 0) {
        return saveFeatures(layerId, bulkUpdateFeatureDto);
      } else {
        return Promise.resolve(); // nothing to update
      }
    }
  }

  _getFeatureDto (featureId) {
    const attributes = getAttributes(this.#formGroups);
    return {
      featureId,
      attributes
    };
  }

  _getBulkUpdateFeatureDto (featureIds) {
    const modifications = getChangedAttributes(this.#formGroups);
    return { featureIds, modifications };
  }

  _getFeatureAttributes () {
    const selectedFeatures = this.selectedLayer.get(LayerProperty.SELECTED_FEATURE_IDS);
    const layerId = this.selectedLayer.get(LayerProperty.ID);
    Promise.all([
      getLayerFeatureAttributes(layerId),
      getLayerFeatureAttributeValues(layerId, selectedFeatures)
    ])
      .then(([ attributeMetaDataJSON, attributeValuesDataJSON ]) => {
        this._initFeatureAttributeInputs(attributeMetaDataJSON, attributeValuesDataJSON);
      }).catch(error => logger.error('Failed to get feature attributes', error));
  }

  _initFeatureAttributeInputs (attributeMetaDataJSON, attributeValuesDataJSON) {
    const attributes = attributeValuesDataJSON?.attributes ?? {};
    this.#initialValues = Object.fromEntries(attributeMetaDataJSON.attributeMetaData
      .map(({ name }) => [ name, attributes[name] ]));
    this.#formGroups = createFormGroups(
      attributeMetaDataJSON,
      this.isMultipleFeatures,
      this.#initialValues
    );
    this.querySelector('.content').append(...this.#formGroups);
  }

  onSave () {
    if (this._isFormValid()) {
      this._saveAttributes().then(() => {
        refreshLayer(this.selectedLayer);
        super.onSave();
      }).catch(errorResponse => this.showErrorsInNotificationArea(
        this._getErrorMessages(errorResponse?.content?.validationMessages)
      ));
    }
  }

  onMapPrimaryInteractionChange () {
    if (this._isFormValid()) {
      this._saveAttributes().catch(error => logger.error('Failed to save feature attributes', error));
    }
    this.close(false);
  }

  _isFormValid () {
    let formValid = this.#formGroups.length > 0;
    this.#formGroups.forEach((formGroup) => {
      if (formGroup.checkValidity() === false) {
        formValid = false;
      }
    });

    return formValid;
  }

  _getErrorMessages (messages = []) {
    return messages.map(notification => {
      switch (notification.type) {
        case NOTIFICATION_TYPE.VALIDATOR :
          return notification.message;
        case NOTIFICATION_TYPE.ATTRIBUTE :
          logger.error('Error while saving attribute', notification.message);
          return i18n('attributeError');
        default:
          throw new Error(`Error ${JSON.stringify(notification)} not supported`);
      }
    });
  }

}

export function createFormGroups (
  attributeMetaDataJSON,
  isMultipleFeatures,
  initialValues = {}
) {
  const formGroups = [];
  attributeMetaDataJSON.attributeMetaData.forEach((attributeMetaDataJSONEntry) => {
    if (!attributeMetaDataJSONEntry.hidden) {
      formGroups.push(new FeatureAttributeFormGroup({
        attributeMetaData: attributeMetaDataJSONEntry,
        isMultipleFeatures,
        initialValue: initialValues[attributeMetaDataJSONEntry.name]
      }));
    }
  });

  return formGroups;
}

export function getAttributes (formGroups, defaultValuesMap) {
  const attributes = (defaultValuesMap === undefined ? {} : defaultValuesMap);
  formGroups.forEach((formGroup) => {
    attributes[formGroup.getFormControlId()] = formGroup.getInputValue();
  });
  return attributes;
}

function getChangedAttributes (formGroups) {
  const attributes = {};
  formGroups.forEach((formGroup) => {
    if (!formGroup.isDisabled) {
      attributes[formGroup.getFormControlId()] = formGroup.getInputValue();
    }
  });
  return attributes;
}

customElements.define(NAME, FeatureAttributesPanel);
